import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalShell from '../components/PortalShell';
import ResponsiveTable from '../components/ui/ResponsiveTable';
import Button from '../components/ui/Button';
import { Globals } from '../globalfunctions.js';
import { apiClient } from '../utils/apiClient';
import { formatMoney } from '../utils/currency';
import { isImpersonating } from '../utils/impersonation';
import './PortalInvoices.css';
import { confirmDialog } from '../utils/dialogs';

function PortalQuotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (Globals.LoginTime === null) {
      localStorage.setItem('redirectAfterLogin', window.location.pathname);
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    (async () => {
      try {
        setQuotes(await apiClient.get('/api/quotes/mine'));
      } catch (error) {
        console.error('Error fetching quotes:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const downloadQuote = async (quote) => {
    setBusyId(quote.id);
    try {
      await apiClient.download(`/api/quotes/mine/${quote.id}/pdf`, `${quote.quote_number}.pdf`);
    } catch (error) {
      setMessage('Could not open that quote. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const respond = async (quote, action) => {
    const verb = action === 'accept' ? 'accept' : 'decline';
    if (!await confirmDialog(`${verb.charAt(0).toUpperCase() + verb.slice(1)} quote ${quote.quote_number} for ${formatMoney(quote.total, quote.currency)}?`)) return;
    setBusyId(quote.id);
    setMessage('');
    try {
      const updated = await apiClient.post(`/api/quotes/mine/${quote.id}/respond`, { action });
      setQuotes((prev) => prev.map((q) => (q.id === quote.id ? updated : q)));
      setMessage(action === 'accept'
        ? `Thanks - quote ${quote.quote_number} is accepted. We'll be in touch shortly to get started.`
        : `Quote ${quote.quote_number} has been declined.`);
    } catch (error) {
      setMessage(error.message || 'Something went wrong. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const columns = [
    { key: 'quote_number', label: 'Quote', sortable: true },
    { key: 'title', label: 'Description' },
    {
      key: 'total',
      label: 'Total',
      sortable: true,
      render: (row) => formatMoney(row.total, row.currency)
    },
    {
      key: 'valid_until',
      label: 'Valid until',
      sortable: true,
      render: (row) => row.valid_until ? new Date(row.valid_until).toLocaleDateString() : '—'
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <span className={`portal-status portal-status-${row.status}`}>{row.status}</span>
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button variant="secondary" onClick={() => downloadQuote(row)} disabled={busyId === row.id}>PDF</Button>
          {row.status === 'sent' && !isImpersonating() && (
            <>
              <Button onClick={() => respond(row, 'accept')} disabled={busyId === row.id}>Accept</Button>
              <Button variant="secondary" onClick={() => respond(row, 'decline')} disabled={busyId === row.id}>Decline</Button>
            </>
          )}
        </div>
      )
    }
  ];

  return (
    <PortalShell>
      <h1>Quotes</h1>
      {message && <p role="status">{message}</p>}
      {loading ? (
        <p>Loading your quotes...</p>
      ) : (
        <ResponsiveTable columns={columns} rows={quotes} rowKey="id" emptyMessage="You don't have any quotes at the moment." />
      )}
    </PortalShell>
  );
}

export default PortalQuotes;
